import React, { useState, useEffect, useContext, useRef } from "react";
import {
   useUser,
   EmailVerification,
   VerificationPending,
   ForgotPassword,
   ResetPassword,
   UserAccount,
   Login,
   Signup,
   AudiobookLoginPrompt,
} from "./User";
import { AudiobookLibrary } from "./Library";
import { PublicFeed } from "./PublicFeed";
import { AdminDashboard } from "./Admin";
import { GlobalAudioPlayer, AudioProvider, AudioContext } from "./AudioPlayer";
import ContinueListening from "./ContinueListening";
import { ThemeProvider, useTheme } from "./ThemeContext"; // Import ThemeProvider
import ThemeToggle from "./ThemeToggle"; // Import ThemeToggle component
import { GoogleOAuthProvider } from "@react-oauth/google";
import AppleAuthCallback from "./AppleAuthCallback";
import { TermsPage } from "./Terms";
import config from "./config";

// Error Boundary Component
class ErrorBoundary extends React.Component {
   constructor(props) {
      super(props);
      this.state = { hasError: false };
   }

   static getDerivedStateFromError(error) {
      return { hasError: true };
   }

   render() {
      if (this.state.hasError) {
         return (
            <div className="alert alert-danger">
               Something went wrong. Please try refreshing the page.
            </div>
         );
      }
      return this.props.children;
   }
}

// Function to parse URL query parameters
const parseUrlParams = () => {
   const params = new URLSearchParams(window.location.search);
   return {
      token: params.get("token"),
      email: params.get("email"),
      action: params.get("action"),
   };
};

// Global fetch handler to intercept unauthorized responses
const setupAuthInterceptor = (logoutFn, setViewFn) => {
   const originalFetch = window.fetch;

   window.fetch = async function (url, options = {}) {
      const response = await originalFetch(url, options);

      if (response.status === 401) {
         // If we get a 401 Unauthorized, the token might be expired
         console.log(
            "Received 401 Unauthorized response. Session may have expired."
         );
         logoutFn();
         setViewFn("login");
      }

      return response;
   };
};

// Create a separate component that can properly access the AudioContext
const AppContent = ({ view, setView }) => {
   const audioContext = useContext(AudioContext);
   const { username, isAdmin, logout, isAuthenticated } = useUser();
   const [dropdownOpen, setDropdownOpen] = useState(false);
   const dropdownRef = useRef(null);
   const { theme } = useTheme(); // Access the current theme

   // This helps with closing the mobile menu when a link is clicked
   useEffect(() => {
      const closeNavbar = () => {
         const navbarContent = document.getElementById("navbarContent");
         if (navbarContent && navbarContent.classList.contains("show")) {
            navbarContent.classList.remove("show");
         }
      };

      // Close mobile menu when view changes
      closeNavbar();

      // Add event listeners to close dropdown when clicking on a nav link
      const navLinks = document.querySelectorAll(".navbar-nav .nav-link");
      navLinks.forEach((link) => {
         link.addEventListener("click", closeNavbar);
      });

      return () => {
         navLinks.forEach((link) => {
            link.removeEventListener("click", closeNavbar);
         });
      };
   }, [view]);

   // Enhanced handle of clicks outside both dropdown and mobile menu
   useEffect(() => {
      const handleClickOutside = (event) => {
         // Handle dropdown closing
         if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
         ) {
            setDropdownOpen(false);
         }

         // Handle mobile menu closing when clicking outside
         const navbar = document.querySelector(".navbar");
         const navbarToggler = document.querySelector(".navbar-toggler");
         const navbarContent = document.getElementById("navbarContent");

         if (
            (navbarContent &&
               navbarContent.classList.contains("show") &&
               !navbar.contains(event.target)) ||
            (navbar.contains(event.target) &&
               !navbarToggler.contains(event.target) &&
               !navbarContent.contains(event.target))
         ) {
            navbarContent.classList.remove("show");
         }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
         document.removeEventListener("mousedown", handleClickOutside);
      };
   }, []);

   // Helper function to navigate and update URL
   const navigateToView = (viewName, updateHistory = true) => {
      console.log(`Navigating to ${viewName} view`);
      setView(viewName);

      // Update browser history if needed
      if (updateHistory) {
         const pathMap = {
            login: "/login",
            signup: "/signup",
            "forgot-password": "/forgot-password",
            "reset-password": "/reset-password",
            "verify-email": "/verify-email",
            public: "/",
            account: "/account",
            library: "/library",
            admin: "/admin",
            generate: "/generate",
            "continue-listening": "/continue-listening", // Add path for continue-listening
         };

         const newPath = pathMap[viewName] || "/";
         window.history.pushState({}, "", newPath);
      }
   };

   // Set the appropriate initial view when the app loads or auth state changes
   useEffect(() => {
      if (isAuthenticated) {
         // If user is authenticated, they can stay on current view
         // No need to change view here as it's handled elsewhere
      } else {
         // If not authenticated and trying to access a protected route, redirect to login
         const protectedViews = [
            "account",
            "library",
            "admin",
            "generate",
            "continue-listening",
         ]; // Add continue-listening to protected views
         if (protectedViews.includes(view)) {
            navigateToView("login");
         }
      }
   }, [isAuthenticated, view]);

   // Check for URL parameters that might trigger specific views
   useEffect(() => {
      // Check if the URL contains parameters for verification or password reset
      const { token, action } = parseUrlParams();
      const path = window.location.pathname;

      // Handle different path patterns
      if (path.includes("/login")) {
         console.log("URL contains /login, setting view to login");
         navigateToView("login", false);
      } else if (token) {
         // Handle different token-based actions
         if (action === "verify" || path.includes("verify-email")) {
            navigateToView("verify-email", false);
         } else if (path.includes("reset-password")) {
            navigateToView("reset-password", false);
         }
      } else if (path.includes("forgot-password")) {
         navigateToView("forgot-password", false);
      } else if (path.includes("resend-verification")) {
         navigateToView("resend-verification", false);
      } else if (path.includes("continue-listening")) {
         // Add handling for continue-listening path
         navigateToView("continue-listening", false);
      } else if (path.includes("generate")) {
         // Map the /generate URL path to the public view
         // Since we now generate in-place, there's no separate generate page
         navigateToView("public", false);
      }
   }, []);

   // Listen for location changes in the URL
   useEffect(() => {
      // Function to detect paths in URL and set view accordingly
      const detectPathAndSetView = () => {
         const path = window.location.pathname;

         // Direct path mapping
         const pathToViewMap = {
            "/login": "login",
            "/signup": "signup",
            "/forgot-password": "forgot-password",
            "/reset-password": "reset-password",
            "/verify-email": "verify-email",
            "/account": "account",
            "/library": "library",
            "/generate": "public", // Map the /generate URL path to public view
            "/admin": "admin",
            "/": "public",
            "/continue-listening": "continue-listening", // Add mapping for continue-listening
            "/auth/apple/callback": "apple-callback",
            "/terms": "terms",
         };

         // Check if we have a direct match
         for (const [urlPath, viewName] of Object.entries(pathToViewMap)) {
            if (path === urlPath) {
               console.log(
                  `Path detected: ${urlPath}, setting view to ${viewName}`
               );
               setView(viewName);
               return;
            }
         }

         // Handle paths with tokens or other parameters
         if (path.includes("/reset-password")) {
            setView("reset-password");
         } else if (path.includes("/verify-email")) {
            setView("verify-email");
         } else if (path.includes("/auth/apple/callback")) {
            navigateToView("apple-callback", false);
         }
      };

      // Detect the path when component mounts or URL changes
      detectPathAndSetView();

      // Setup event listener for URL changes
      const handleLocationChange = () => {
         detectPathAndSetView();
      };

      window.addEventListener("popstate", handleLocationChange);

      // Clean up
      return () => {
         window.removeEventListener("popstate", handleLocationChange);
      };
   }, []);

   // Setup fetch interceptor to catch 401 responses
   useEffect(() => {
      setupAuthInterceptor(logout, navigateToView);
   }, [logout]);

   // Set up a session expiry check that runs periodically
   useEffect(() => {
      if (!isAuthenticated) return;

      // Check token expiration every minute
      const checkTokenExpiry = () => {
         const token = localStorage.getItem("token");
         if (!token) {
            logout();
            navigateToView("login");
            return;
         }

         try {
            // Parse the JWT to check expiration
            const tokenParts = token.split(".");
            if (tokenParts.length !== 3) {
               // Not a valid JWT format
               logout();
               navigateToView("login");
               return;
            }

            const payload = JSON.parse(atob(tokenParts[1]));
            // Check if token has expired
            if (payload.exp && payload.exp * 1000 < Date.now()) {
               console.log("Session expired. Redirecting to login.");
               logout();
               navigateToView("login");
            }
         } catch (error) {
            console.error("Error checking token expiry:", error);
            logout();
            navigateToView("login");
         }
      };

      // Run initial check
      checkTokenExpiry();

      // Set up interval for checking (every minute)
      const interval = setInterval(checkTokenExpiry, 60000);

      // Clean up interval on component unmount
      return () => clearInterval(interval);
   }, [isAuthenticated, logout]);

   // Check for pending audiobook to play after components are mounted
   useEffect(() => {
      // Check after the component mounts if there's a pending audiobook
      const pendingAudiobookStr = localStorage.getItem("pendingAudiobookData");
      const shouldAutoPlay = sessionStorage.getItem("autoPlayAfterLogin");

      console.log("App.js checking for pending audiobook:", {
         pendingAudiobook: pendingAudiobookStr ? "exists" : "none",
         shouldAutoPlay,
         isAuthenticated,
      });

      if (
         pendingAudiobookStr &&
         shouldAutoPlay &&
         isAuthenticated &&
         audioContext &&
         audioContext.loadBook
      ) {
         try {
            const pendingAudiobook = JSON.parse(pendingAudiobookStr);
            console.log(
               "App.js: Found pending audiobook in localStorage, will attempt to play:",
               pendingAudiobook.title || pendingAudiobook.uuid
            );

            // Restore to window object temporarily in case other components need it
            window.pendingAudiobook = pendingAudiobook;

            // We won't play it directly here, as the PublicFeed component
            // should handle this when it loads. We're just ensuring the data
            // is available.
         } catch (error) {
            console.error("Error processing stored audiobook:", error);
         }
      }
   }, [audioContext, isAuthenticated]);

   // Add click outside listener to close dropdown when clicking outside
   useEffect(() => {
      const handleClickOutside = (event) => {
         if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
         ) {
            setDropdownOpen(false);
         }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
         document.removeEventListener("mousedown", handleClickOutside);
      };
   }, []);

   const handleLogout = () => {
      try {
         // Close dropdown
         setDropdownOpen(false);

         // Clear current audio playback first
         if (audioContext) {
            // First pause any playing audio
            if (
               audioContext.playbackState &&
               audioContext.playbackState.isPlaying
            ) {
               audioContext.togglePlayPause();
            }

            // Force save any current progress before clearing
            if (audioContext.currentBook && audioContext.forceSaveProgress) {
               audioContext.forceSaveProgress("logout");
            }

            // Reset the current book in the context
            if (audioContext.updateCurrentAudio) {
               audioContext.updateCurrentAudio(null);
            }
         }

         // Clear all user-related data
         logout();

         // Clear any additional storage items
         const itemsToClear = [
            "preferences",
            "librarySettings",
            "lastLoginTime",
         ];

         itemsToClear.forEach((item) => {
            localStorage.removeItem(item);
         });

         // Clear any active timers
         if (window.activeTimers) {
            window.activeTimers.forEach((timer) => clearTimeout(timer));
         }

         // Close any active connections
         if (window.activeSocket) {
            window.activeSocket.close();
         }

         // Reset view to login page after logout
         navigateToView("login");

         // Reset page title
         document.title = "My Audiobook App";
      } catch (error) {
         console.error("Logout error:", error);
         alert("There was an error during logout. Please try again.");
      }
   };

   const toggleDropdown = () => {
      setDropdownOpen(!dropdownOpen);
   };

   const handleLoginSuccess = () => {
      console.log("Login successful, redirecting to public feed");

      // Check for pending audiobook using our persistent key
      const pendingAudiobookStr = localStorage.getItem("pendingAudiobookData");
      if (pendingAudiobookStr) {
         console.log(
            "Found pending audiobook after login, ensuring autoplay flag is set"
         );
         sessionStorage.setItem("autoPlayAfterLogin", "true");

         try {
            // Restore to window object
            window.pendingAudiobook = JSON.parse(pendingAudiobookStr);
            console.log(
               "Pending audiobook restored to window object:",
               window.pendingAudiobook.title || window.pendingAudiobook.uuid
            );
         } catch (error) {
            console.error("Error parsing stored audiobook:", error);
         }
      } else {
         console.log("No pending audiobook found after login");
      }

      // Redirect to public feed
      navigateToView("public");
   };

   const renderView = () => {
      // Get token from URL if present (for direct links)
      const urlParams = new URLSearchParams(window.location.search);
      const verificationToken = urlParams.get("token");
      const resetToken = urlParams.get("token");

      // If we have a token in the URL and are on the verify-email path, show verification
      if (
         window.location.pathname.includes("verify-email") &&
         verificationToken
      ) {
         return <EmailVerification />;
      }

      // If we have a token in the URL and are on the reset-password path, show reset password form
      if (window.location.pathname.includes("reset-password") && resetToken) {
         console.log(
            "Rendering ResetPassword component with setView function",
            typeof navigateToView
         );
         return <ResetPassword setView={navigateToView} />;
      }

      // Protect routes that require authentication
      const protectedViews = [
         "account",
         "library",
         "admin",
         "generate",
         "continue-listening",
      ]; // Add continue-listening to protected views
      if (protectedViews.includes(view) && !isAuthenticated) {
         return <Login onLogin={handleLoginSuccess} setView={navigateToView} />;
      }

      // Check if we need to show the audiobook login prompt
      if (view === "login" && window.loginReason === "audiobook_access") {
         return (
            <AudiobookLoginPrompt
               onLogin={handleLoginSuccess}
               setView={navigateToView}
            />
         );
      }

      switch (view) {
         case "signup":
            return (
               <Signup
                  onSignup={() => {
                     // Always go to the public feed after signup
                     navigateToView("public");
                  }}
                  setView={navigateToView}
               />
            );
         case "login":
            return (
               <Login onLogin={handleLoginSuccess} setView={navigateToView} />
            );
         case "account":
            return <UserAccount />;
         case "library":
            return <AudiobookLibrary />;
         case "public":
            return <PublicFeed />;
         case "admin":
            return <AdminDashboard />;
         case "apple-callback":
            return <AppleAuthCallback />;
         case "generate":
            // Show the public feed with the AudiobookPromptSelector - which now includes generation
            return <PublicFeed />;
         case "continue-listening":
            return <ContinueListening />;
         // Email verification views
         case "verify-email":
            return <EmailVerification />;
         case "verification-pending":
            return <VerificationPending />;
         // Password reset views
         case "forgot-password":
            console.log(
               "Rendering ForgotPassword from switch case with setView function",
               typeof navigateToView
            );
            return <ForgotPassword setView={navigateToView} />;
         case "reset-password":
            console.log(
               "Rendering ResetPassword from switch case with setView function",
               typeof navigateToView
            );
            return <ResetPassword setView={navigateToView} />;
         case "terms":
            return <TermsPage />;
         default:
            return <PublicFeed />;
      }
   };
   return (
      <>
         <nav
            className={`navbar navbar-expand-lg ${
               theme === "light"
                  ? "navbar-light bg-light"
                  : "navbar-dark bg-dark"
            } shadow-sm mb-4`}
         >
            <div className="container-fluid px-6">
               <a
                  className="navbar-brand"
                  href="#"
                  onClick={(e) => {
                     e.preventDefault();
                     navigateToView("public");
                  }}
               >
                  <span className="pageshift-logo fw-bold">Pageshift </span>
                  <span className="beta-version-tag fst-italic">
                     Beta Version
                  </span>
               </a>

               {/* Hamburger menu button for mobile view */}
               <button
                  className="navbar-toggler"
                  type="button"
                  aria-controls="navbarContent"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                  onClick={() => {
                     // Toggle the mobile menu manually
                     const navbarContent =
                        document.getElementById("navbarContent");
                     if (navbarContent) {
                        navbarContent.classList.toggle("show");
                     }
                  }}
               >
                  <span className="navbar-toggler-icon"></span>
               </button>

               <div className="collapse navbar-collapse" id="navbarContent">
                  {isAuthenticated ? (
                     <>
                        <div className="navbar-nav me-auto">
                           <button
                              className={`nav-item btn btn-link nav-link ${
                                 view === "public"
                                    ? "text-warning"
                                    : theme === "light"
                                    ? "text-dark"
                                    : "text-light"
                              }`}
                              onClick={() => {
                                 navigateToView("public");
                                 // Close mobile menu when clicking a link
                                 const navbarContent =
                                    document.getElementById("navbarContent");
                                 if (navbarContent && window.innerWidth < 992) {
                                    navbarContent.classList.remove("show");
                                 }
                              }}
                           >
                              Find a Story
                           </button>
                           <button
                              className={`nav-item btn btn-link nav-link ${
                                 view === "library"
                                    ? "text-warning"
                                    : theme === "light"
                                    ? "text-dark"
                                    : "text-light"
                              }`}
                              onClick={() => {
                                 navigateToView("library");
                                 // Close mobile menu when clicking a link
                                 const navbarContent =
                                    document.getElementById("navbarContent");
                                 if (navbarContent && window.innerWidth < 992) {
                                    navbarContent.classList.remove("show");
                                 }
                              }}
                           >
                              My Stories
                           </button>
                           <button
                              className={`nav-item btn btn-link nav-link ${
                                 view === "continue-listening" // Update class check for continue-listening
                                    ? "text-warning"
                                    : theme === "light"
                                    ? "text-dark"
                                    : "text-light"
                              }`}
                              onClick={() => {
                                 navigateToView("continue-listening"); // Update to navigate to continue-listening
                                 // Close mobile menu when clicking a link
                                 const navbarContent =
                                    document.getElementById("navbarContent");
                                 if (navbarContent && window.innerWidth < 992) {
                                    navbarContent.classList.remove("show");
                                 }
                              }}
                           >
                              Continue listening
                           </button>
                           {isAdmin && (
                              <button
                                 className={`nav-item btn btn-link nav-link ${
                                    view === "admin"
                                       ? "text-warning"
                                       : theme === "light"
                                       ? "text-dark"
                                       : "text-light"
                                 }`}
                                 onClick={() => {
                                    navigateToView("admin");
                                    // Close mobile menu when clicking a link
                                    const navbarContent =
                                       document.getElementById("navbarContent");
                                    if (
                                       navbarContent &&
                                       window.innerWidth < 992
                                    ) {
                                       navbarContent.classList.remove("show");
                                    }
                                 }}
                              >
                                 Admin
                              </button>
                           )}
                        </div>

                        {/* User dropdown menu - moved inside the collapsible div for mobile */}
                        <div className="user-profile-section">
                           {/* Theme Toggle Button */}
                           <div style={{ marginRight: "15px" }}>
                              <ThemeToggle />
                           </div>

                           <div
                              className="dropdown ms-lg-0 mt-2 mt-lg-0"
                              ref={dropdownRef}
                           >
                              <div
                                 className="user-avatar rounded-circle bg-light d-flex align-items-center d-none d-lg-flex"
                                 style={{
                                    width: "40px",
                                    height: "40px",
                                    cursor: "pointer",
                                 }}
                                 onClick={toggleDropdown}
                              >
                                 <span className="fw-bold text-dark">
                                    {username
                                       ? username.charAt(0).toUpperCase()
                                       : "J"}
                                 </span>
                              </div>

                              {/* Mobile profile links - shown directly in hamburger menu */}
                              <div className="mobile-profile-links d-lg-none">
                                 <button
                                    className="dropdown-item-mobile px-3 py-2 text-light w-100 text-start btn btn-link"
                                    onClick={() => {
                                       navigateToView("account");
                                       // Close mobile menu
                                       const navbarContent =
                                          document.getElementById(
                                             "navbarContent"
                                          );
                                       if (navbarContent) {
                                          navbarContent.classList.remove(
                                             "show"
                                          );
                                       }
                                    }}
                                 >
                                    My Account
                                 </button>
                                 <button
                                    className="dropdown-item-mobile px-3 py-2 text-light w-100 text-start btn btn-link"
                                    onClick={() => {
                                       navigateToView("library");
                                       // Close mobile menu
                                       const navbarContent =
                                          document.getElementById(
                                             "navbarContent"
                                          );
                                       if (navbarContent) {
                                          navbarContent.classList.remove(
                                             "show"
                                          );
                                       }
                                    }}
                                 >
                                    My Stories
                                 </button>
                                 <button
                                    className="dropdown-item-mobile px-3 py-2 text-danger w-100 text-start btn btn-link"
                                    onClick={() => {
                                       handleLogout();
                                       // Close mobile menu
                                       const navbarContent =
                                          document.getElementById(
                                             "navbarContent"
                                          );
                                       if (navbarContent) {
                                          navbarContent.classList.remove(
                                             "show"
                                          );
                                       }
                                    }}
                                 >
                                    Logout
                                 </button>
                              </div>

                              {/* Desktop dropdown menu - only shown on larger screens */}
                              {dropdownOpen && (
                                 <div
                                    className="dropdown-menu dropdown-menu-end show d-none d-lg-block"
                                    style={{
                                       position: "absolute",
                                       top: "50px",
                                       right: "0",
                                       zIndex: 1000,
                                    }}
                                 >
                                    <div className="dropdown-item-text px-4 py-2">
                                       <div className="fw-bold">
                                          Hi, {username || "User"}
                                       </div>
                                    </div>
                                    <div className="dropdown-divider"></div>
                                    <button
                                       className="dropdown-item px-4 py-2"
                                       onClick={() => {
                                          setDropdownOpen(false);
                                          navigateToView("account");
                                       }}
                                    >
                                       My Account
                                    </button>
                                    <button
                                       className="dropdown-item px-4 py-2"
                                       onClick={() => {
                                          setDropdownOpen(false);
                                          navigateToView("library");
                                       }}
                                    >
                                       My Stories
                                    </button>
                                    <div className="dropdown-divider"></div>
                                    <button
                                       className="dropdown-item text-danger px-4 py-2"
                                       onClick={handleLogout}
                                    >
                                       Logout
                                    </button>
                                 </div>
                              )}
                           </div>
                        </div>
                     </>
                  ) : (
                     <>
                        <div className="navbar-nav me-auto">
                           <button
                              className={`nav-item btn btn-link nav-link ${
                                 view === "public"
                                    ? "text-warning"
                                    : theme === "light"
                                    ? "text-dark"
                                    : "text-light"
                              }`}
                              onClick={() => navigateToView("public")}
                           >
                              Find a Story
                           </button>
                           <button
                              className={`nav-item btn btn-link nav-link ${
                                 view === "login"
                                    ? "text-warning"
                                    : theme === "light"
                                    ? "text-dark"
                                    : "text-light"
                              }`}
                              onClick={() => navigateToView("login")}
                           >
                              Login
                           </button>
                           <button
                              className={`nav-item btn btn-link nav-link ${
                                 view === "signup"
                                    ? "text-warning"
                                    : theme === "light"
                                    ? "text-dark"
                                    : "text-light"
                              }`}
                              onClick={() => navigateToView("signup")}
                           >
                              Sign Up
                           </button>
                        </div>
                        {/* Theme Toggle for unauthenticated users */}
                        <div>
                           <ThemeToggle />
                        </div>
                     </>
                  )}
               </div>
            </div>
         </nav>
         <ErrorBoundary>
            <div className="page-container">{renderView()}</div>
         </ErrorBoundary>
         {/* Global Audio Player - Now uses the current view for visibility control */}
         <GlobalAudioPlayer currentView={view} />
      </>
   );
};

function App() {
   // Default view is "public" instead of "login"
   const [view, setView] = useState("public");

   // Expose setView to window for enhanced components
   useEffect(() => {
      window.setView = setView;
      return () => {
         delete window.setView;
      };
   }, []);

   // Add auth-page class to body when on login or signup pages
   useEffect(() => {
      if (
         view === "login" ||
         view === "signup" ||
         view === "verify-email" ||
         view === "resend-verification" ||
         view === "verification-pending" ||
         view === "forgot-password" ||
         view === "reset-password"
      ) {
         document.body.classList.add("auth-page");
      } else {
         document.body.classList.remove("auth-page");
      }

      return () => {
         document.body.classList.remove("auth-page");
      };
   }, [view]);

   // Check for URL-based actions on initial load
   useEffect(() => {
      // Parse the URL to determine if we need to set a specific view
      const parseUrlParams = () => {
         const params = new URLSearchParams(window.location.search);
         return {
            token: params.get("token"),
            email: params.get("email"),
            action: params.get("action"),
         };
      };

      const { token, action } = parseUrlParams();
      const path = window.location.pathname;

      // Handle token-based actions
      if (token) {
         if (action === "verify" || path.includes("verify-email")) {
            setView("verify-email");
         } else if (path.includes("reset-password")) {
            setView("reset-password");
         }
      }
      // Handle path-based actions
      else if (path.includes("/login")) {
         setView("login");
      } else if (path.includes("forgot-password")) {
         setView("forgot-password");
      } else if (path.includes("resend-verification")) {
         setView("resend-verification");
      } else if (path.includes("continue-listening")) {
         setView("continue-listening");
      } else if (path.includes("generate")) {
         setView("public");
      }
   }, []);

   return (
      <GoogleOAuthProvider clientId={config.socialAuth.googleClientId}>
         <ThemeProvider>
            <AudioProvider>
                     <div
                        className={`container-fluid px-0 ${
                           view === "login" ||
                           view === "signup" ||
                           view === "verify-email" ||
                           view === "resend-verification" ||
                           view === "verification-pending" ||
                           view === "forgot-password" ||
                           view === "reset-password"
                              ? "auth-container"
                              : "mt-0"
                        }`}
                     >
                        <div className="background-container">
                           <div className="background-base"></div>
                           <div className="background-top-gradient"></div>
                           <AppContent view={view} setView={setView} />
                        </div>
                     </div>
            </AudioProvider>
         </ThemeProvider>
      </GoogleOAuthProvider>
   );
}

export default App;
